import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  type User,
} from 'firebase/auth';
import { useCallback, useEffect, useState } from 'react';
import { auth } from '../../lib/firebase';

/**
 * Admin app の認証 hook (Sprint admin 第 9 弾、Phase 5.4)。
 *
 * 旧 admin は Email + Password + Google OAuth で sign-in、master / districtAdmin の
 * custom claim で role 識別。v2 minimum では Email + Password のみ実装 (Google OAuth は後続)。
 *
 * 役割:
 *   - sign-in (email/password) + sign-out
 *   - 現在の auth state 配信 (user / role / accessibleDistricts)
 *   - idToken の claims を解析して role を取り出す (rules 側で deny されるので念のため UI でも guard)
 *
 * 旧との差分:
 *   - Google OAuth は scope 外 (admin app 第 10 弾以降)
 *   - パスワードリセット UI は admin 限定なので別 sprint
 *   - 「地区切替」セレクタは accessibleDistricts > 1 のときのみ表示 (current Phase は単一想定)
 */

export type AdminRole = 'master' | 'district' | null;

export interface AdminAuthState {
  /** firebase user (sign-in 済) または null。 */
  user: User | null;
  /** custom claim から取得した role (`master` / `district` / null)。 */
  role: AdminRole;
  /** custom claim `accessibleDistricts` (参加者 urlToken 解決済の地区、用途は user app 用)。 */
  accessibleDistricts: string[];
  /**
   * custom claim `districts` (district admin role 用の担当地区配列、rules `isDistrictAdmin` 参照)。
   * master role では空配列 (master は全地区アクセス可)。admin 第 11 弾 (posts moderation) で利用開始。
   */
  districts: string[];
  /** initial load 完了フラグ (onAuthStateChanged callback 後 true)。 */
  ready: boolean;
}

const INITIAL_STATE: AdminAuthState = {
  user: null,
  role: null,
  accessibleDistricts: [],
  districts: [],
  ready: false,
};

interface TokenClaims {
  role?: string;
  accessibleDistricts?: string[];
  districts?: string[];
}

/**
 * Sprint BB Part 1: admin sign-in を許可する email domain の allowlist (環境変数
 * `VITE_ALLOWED_ADMIN_DOMAINS`、カンマ区切り)。未設定または空文字なら制限なし (旧 admin と同
 * default、Custom Claims による server 側 role guard が本命であり、本 allowlist は早期 UX
 * 改善のための soft guard)。
 *
 * pure 関数として export し unit test 可能化。null/undefined/空文字を「制限なし」扱い、
 * 大文字小文字を無視し、`@y-p-c.co.jp` のように `@` 始まりも前置きを除去して比較。
 */
export function isAdminEmailAllowed(
  email: string | null | undefined,
  allowlistRaw: string | null | undefined,
): boolean {
  const list = (allowlistRaw ?? '')
    .split(',')
    .map((s) => s.trim().toLowerCase().replace(/^@/, ''))
    .filter((s) => s.length > 0);
  if (list.length === 0) return true; // 未設定 = 制限なし
  if (!email || typeof email !== 'string') return false;
  const at = email.lastIndexOf('@');
  if (at < 0) return false;
  const domain = email.slice(at + 1).toLowerCase();
  return list.includes(domain);
}

/**
 * Google OAuth エラー code → 日本語メッセージへの正規化 (旧 LoginPage.tsx 踏襲、admin 第 24 弾)。
 * pure 関数として export し unit test 可能化。
 */
export function normalizeGoogleSignInError(
  code: string | undefined,
  hostname: string | undefined,
  rawMessage: string,
): { silent: boolean; message: string } {
  const c = code ?? '';
  // ユーザーが popup を閉じた / 二重開きで cancel された場合は silent (UI に error 表示しない)
  if (c === 'auth/popup-closed-by-user' || c === 'auth/cancelled-popup-request') {
    return { silent: true, message: '' };
  }
  if (c === 'auth/unauthorized-domain') {
    return {
      silent: false,
      message: `このドメイン (${hostname ?? 'unknown'}) が Firebase Auth の承認済みドメインに登録されていません。管理者に連絡してください。`,
    };
  }
  if (c === 'auth/popup-blocked') {
    return {
      silent: false,
      message:
        'ブラウザがポップアップをブロックしました。ポップアップを許可して再試行してください。',
    };
  }
  if (c === 'auth/operation-not-allowed') {
    return {
      silent: false,
      message: 'Google ログインが Firebase で有効化されていません。管理者に連絡してください。',
    };
  }
  return {
    silent: false,
    message: `Google ログインに失敗しました (${c || 'unknown'}): ${rawMessage}`,
  };
}

export function useAdminAuth(): {
  state: AdminAuthState;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOutAdmin: () => Promise<void>;
  error: Error | null;
} {
  const [state, setState] = useState<AdminAuthState>(INITIAL_STATE);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setState({
          user: null,
          role: null,
          accessibleDistricts: [],
          districts: [],
          ready: true,
        });
        return;
      }
      // role / districts の custom claim は master が後から付与・変更するため、cache された
      // ID token を使うと「割当済なのに本人の画面に反映されない」ズレが起きる
      // (例: 地区管理者を割り当てたのに担当地区一覧に箱根が出ない)。auth state 変化のたびに
      // force=true で token を取り直し、最新 claim を確実に反映する。
      // 追加コストは load / signIn ごとに 1 回の token refresh のみ (admin app では許容)。
      const idTokenResult = await user.getIdTokenResult(true);
      const claims = (idTokenResult.claims as unknown as TokenClaims) ?? {};
      const role: AdminRole =
        claims.role === 'master' ? 'master' : claims.role === 'district' ? 'district' : null;
      const accessibleDistricts = Array.isArray(claims.accessibleDistricts)
        ? claims.accessibleDistricts
        : [];
      const districts = Array.isArray(claims.districts) ? claims.districts : [];
      setState({ user, role, accessibleDistricts, districts, ready: true });
    });
    return () => unsub();
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    setError(null);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (e) {
      const raw = e as { code?: string; message?: string };
      let message = raw.message ?? String(e);
      const code = typeof raw.code === 'string' ? raw.code : '';
      if (
        code.includes('user-not-found') ||
        code.includes('wrong-password') ||
        code.includes('invalid-credential')
      ) {
        message = 'メールアドレスまたはパスワードが正しくありません。';
      } else if (code.includes('too-many-requests')) {
        message = '一時的にサインインが制限されています。しばらく経ってから再試行してください。';
      }
      const err = new Error(message);
      (err as Error & { code?: string }).code = code;
      setError(err);
      throw err;
    }
  }, []);

  const signInWithGoogle = useCallback(async () => {
    setError(null);
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      const cred = await signInWithPopup(auth, provider);
      // Sprint BB Part 1: VITE_ALLOWED_ADMIN_DOMAINS allowlist による soft guard。
      // 本命は Custom Claims (server 側 requireMaster/requireAdmin) だが、
      // 不正 domain は signIn 直後に signOut + 明示メッセージで早期に弾く UX。
      const allowlistRaw = (import.meta.env.VITE_ALLOWED_ADMIN_DOMAINS as string | undefined) ?? '';
      if (!isAdminEmailAllowed(cred.user.email, allowlistRaw)) {
        const email = cred.user.email ?? 'unknown';
        await signOut(auth).catch(() => {
          /* ignore */
        });
        const err = new Error(
          `この email (${email}) は許可された admin ドメインに含まれていません。管理者に連絡してください。`,
        );
        (err as Error & { code?: string }).code = 'auth/domain-not-allowed';
        setError(err);
        throw err;
      }
    } catch (e) {
      const raw = e as { code?: string; message?: string };
      const code = typeof raw.code === 'string' ? raw.code : '';
      // Sprint BB Part 1: domain-not-allowed は自分で setError 済 + 専用メッセージ済なので
      // normalizer をスキップして re-throw のみ。
      if (code === 'auth/domain-not-allowed') {
        throw e;
      }
      const hostname = typeof window !== 'undefined' ? window.location.hostname : undefined;
      const { silent, message } = normalizeGoogleSignInError(
        code,
        hostname,
        raw.message ?? String(e),
      );
      if (silent) return;
      const err = new Error(message);
      (err as Error & { code?: string }).code = code;
      setError(err);
      throw err;
    }
  }, []);

  const signOutAdmin = useCallback(async () => {
    setError(null);
    await signOut(auth);
  }, []);

  // セルフのパスワード再発行 (sendPasswordReset) は Option C (docs/110) で廃止。
  // 再発行メールの着地ドメインをコードで是正できず (callbackUri ロック)、authDomain 書換は
  // Google OAuth を壊すため、復旧はマスターの再発行リンク (/auth/invite、カスタムドメイン +
  // 期限指定 + 自動ログイン) に一本化した。SignInPage は「マスターに依頼」案内のみ表示する。

  return { state, signIn, signInWithGoogle, signOutAdmin, error };
}
